import {
  ConfigurableOptions,
  ConversationType,
  CustomerRateType,
  JobFragment,
  LatLng,
  Maybe,
  OriginatorStatus,
  QuoteBidStatus,
  RatingMethod,
} from '@shared/generated/graphql';
import { StandardMileageUnit } from 'clerk_common';
import { CompletedFreightOrderTemplate } from 'clerk_common/templates/freight_order/types';
import { OriginatorSettings, Rate } from 'clerk_common/types';
import { ExternalIdMapping } from './external-id-mapping';
import { ConfigOverrides, IntegrationConfigs } from './order';

type StandardMileage = {
  value: number;
  units: StandardMileageUnit;
};

export type MileageRate = {
  value: number;
  units: StandardMileageUnit;
};

export enum DATRatingMethod {
  FORECAST = 'FORECAST',
  HISTORY = 'HISTORY',
  RATE = 'RATE',
}

export enum TriumphRatingMethod {
  DEFAULT = 'DEFAULT',
  NETWORK = 'NETWORK',
}

export enum DrayratesRatingMethod {
  MARKET = 'market',
  BUY = 'buy',
}

export enum TruckstopRatingMethod {
  BOOKED = 'BOOKED',
  POSTED = 'POSTED',
}

export type RateMetadata = {
  mileage?: StandardMileage;
  mileageRate?: MileageRate;
  responseId?: string;
  rateId?: string;
  url?: string;
  response?: any;
  label?: string;
  key?: string;
  ratingMethod?:
    | DATRatingMethod
    | DrayratesRatingMethod
    | TriumphRatingMethod
    | TruckstopRatingMethod;
  pricingStrategyReasoning?: string;
};

export type Carrier = {
  id: string;
  createdAt: string;
  name: string;
  dotNumber: string;
  mcNumber?: string;
  isInNetwork?: boolean;
  contacts?: Contact[];
};

export type Contact = {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
};

export type CarrierRateAction = {
  output?: Record<string, unknown> | null;
};

export type CarrierRateConversation = {
  id: string;
  type: ConversationType;
  summary?: {
    summary?: string;
  };
  actions?: CarrierRateAction[];
};

export type CarrierRate = {
  id: string;
  createdAt: string;
  ratingMethod: RatingMethod;
  computedRate?: Rate | null;
  metadata?: RateMetadata;
  carrier?: Carrier;
  contact?: Contact;
  conversation?: CarrierRateConversation;
};

export type CustomerRate = {
  id: string;
  createdAt: string;
  type: CustomerRateType;
  ratingMethod?: RatingMethod;
  rate?: Rate | null;
  metadata?: RateMetadata;
};

export type Quote = {
  id: string;
  createdAt: string;
  status?: Maybe<QuoteBidStatus> | undefined;
  extractedData?: CompletedFreightOrderTemplate;
  customFreightTags?: string[];
  organization: {
    id: string;
    name?: string;
    integrationConfigs?: IntegrationConfigs;
    configurableOptions: ConfigurableOptions;
    settings?: {
      customFreightTags?: string[];
    };
  };
  originator?: {
    id?: string;
    name?: string;
    transmitIds: string[];
    status: OriginatorStatus;
    configOverrides?: ConfigOverrides;
    settings?: OriginatorSettings;
    externalIdMappings?: ExternalIdMapping[];
  };
  carrierRates?: CarrierRate[];
  customerRates?: CustomerRate[];
  carrierRate?: CarrierRate;
  customerRate?: CustomerRate;
  routeGeoCoordinates?: LatLng[];
  googleMapsEmbedUrl?: string | null;
  job?: JobFragment;
  notesCount: number;
  externalDeepLink?: string;
  shipmentId?: string;
  mileageBetweenStops?: number[];
};

export type QuotePreview = {
  id: string;
  createdAt: Date;
  createdAtDisplay: Date;
  updatedAt: Date;
  originator: {
    id?: string;
    name?: string;
  };
  user?: {
    id: string;
    email?: string;
  };
};
